import {
  useCallback,
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';

// Shared across all hook instances so StrictMode double-effects and sibling
// consumers of the same cache key issue a single request.
const inFlightFetches = new Map(); // `${namespace}:${key}` -> Promise

function startSharedFetch(cache, key, fetcher) {
  const flightKey = `${cache.namespace}:${key}`;
  let promise = inFlightFetches.get(flightKey);
  if (!promise) {
    promise = Promise.resolve()
      .then(() => fetcher())
      .then((result) => {
        cache.set(key, result);
        return result;
      })
      .finally(() => {
        if (inFlightFetches.get(flightKey) === promise) {
          inFlightFetches.delete(flightKey);
        }
      });
    inFlightFetches.set(flightKey, promise);
  }
  return promise;
}

/**
 * Stale-while-revalidate bridge between a listCache store and React.
 *
 * Fresh cache entry  -> render instantly (no loading), revalidate silently.
 * Missing/expired    -> loading until the fetcher resolves.
 * refresh()          -> force refetch; cached rows stay visible meanwhile.
 * mutate(updater)    -> immediate local update of the cached list.
 *
 * @param {{ cache: import('../utils/listCache/listCacheStore').createListCache extends (c: any) => infer R ? R : never,
 *           key: string, fetcher: () => Promise<any>, enabled?: boolean }} opts
 */
export function useCachedList({ cache, key, fetcher, enabled = true }) {
  const entry = useSyncExternalStore(cache.subscribe, () =>
    key == null ? undefined : cache.peek(key)
  );
  const [isRevalidating, setIsRevalidating] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState(null);

  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;
  // Invalidates in-flight resolutions when the key/enabled inputs change.
  const effectTokenRef = useRef(0);

  useEffect(() => {
    if (!enabled || key == null || key === '') return;
    const token = ++effectTokenRef.current;
    // get() lazily purges an expired entry, dropping us to the cold path.
    const hasFreshEntry = cache.get(key) !== undefined;
    if (hasFreshEntry) setIsRevalidating(true);

    startSharedFetch(cache, key, fetcherRef.current)
      .then(() => {
        if (effectTokenRef.current === token) setError(null);
      })
      .catch((err) => {
        if (effectTokenRef.current === token) setError(err);
      })
      .finally(() => {
        if (effectTokenRef.current === token) setIsRevalidating(false);
      });
  }, [cache, key, enabled]);

  const refresh = useCallback(async () => {
    if (key == null || key === '') return;
    const token = ++effectTokenRef.current;
    setIsRefreshing(true);
    try {
      // Bypasses the dedupe map on purpose: refresh must hit the network.
      const result = await fetcherRef.current();
      cache.set(key, result);
      if (effectTokenRef.current === token) setError(null);
    } catch (err) {
      if (effectTokenRef.current === token) setError(err);
    } finally {
      setIsRefreshing(false);
    }
  }, [cache, key]);

  const mutate = useCallback(
    (updater) => {
      if (key == null) return;
      cache.mutateEntry(key, updater);
    },
    [cache, key]
  );

  const data = entry ? entry.data : undefined;
  const loading = Boolean(enabled) && data === undefined && !error;

  return { data, loading, isRevalidating, isRefreshing, error, refresh, mutate };
}
